/**
 * Codebase Coherence Check — MCP Integration Module.
 * BET-99 / FEAT-182 / ARCH-19
 *
 * Orchestrates the coherence pipeline: resolve registries from source files,
 * evaluate rules, produce markdown for orient/start, snapshot for persistence.
 *
 * Canonical engine: src/lib/coherence/ (ARCH-16).
 * This module syncs types, engine, and data; adds the filesystem resolver
 * and rendering that are MCP-specific.
 */

import { REGISTRY_MANIFEST, COLLECTION_REGISTRY_RULES } from "./data.js";
import { evaluateCoherence, toSnapshot } from "./engine.js";
import { resolveRegistries } from "./resolver.js";
import type { CoherenceReport, CoherenceSnapshot, CoherenceViolation, RuleSeverity } from "./types.js";

export type { CoherenceReport, CoherenceSnapshot, CoherenceViolation } from "./types.js";
export { toSnapshot } from "./engine.js";
export { detectTouchedRegistries, hasRegistryChanges } from "./git-detection.js";

/**
 * Run the full coherence check pipeline.
 * Returns null if resolution fails (e.g. running outside a workspace).
 */
export function runCoherenceCheck(projectRoot: string): CoherenceReport | null {
  const resolved = resolveRegistries(projectRoot, REGISTRY_MANIFEST);

  // Need at least 2 resolved registries for any rule to evaluate
  if (Object.keys(resolved).length < 2) return null;

  return evaluateCoherence(REGISTRY_MANIFEST, COLLECTION_REGISTRY_RULES, resolved);
}

// ── Orient / Start rendering ──────────────────────────────────────────────────

export function renderCoherenceLines(report: CoherenceReport): string[] {
  const lines: string[] = ["## Codebase Coherence"];

  if (report.summary.total === 0) {
    lines.push("All registries aligned — no gaps detected.");
    lines.push("");
    return lines;
  }

  const parts: string[] = [];
  if (report.summary.errors > 0) parts.push(`${report.summary.errors} error${report.summary.errors === 1 ? "" : "s"}`);
  if (report.summary.warnings > 0) parts.push(`${report.summary.warnings} warning${report.summary.warnings === 1 ? "" : "s"}`);
  if (report.summary.infos > 0) parts.push(`${report.summary.infos} info`);
  lines.push(`**${report.summary.total} gap${report.summary.total === 1 ? "" : "s"}** across ${report.totalRegistries} registries (${parts.join(", ")})`);

  const errors = report.violations.filter((v) => v.severity === "error");
  const warnings = report.violations.filter((v) => v.severity === "warning");

  if (errors.length > 0) {
    lines.push("");
    lines.push("**Errors** (must fix):");
    for (const v of errors.slice(0, 5)) {
      lines.push(`- ${v.message}`);
    }
    if (errors.length > 5) lines.push(`- ...and ${errors.length - 5} more`);
  }

  if (warnings.length > 0) {
    lines.push("");
    lines.push("**Warnings**:");
    for (const v of warnings.slice(0, 3)) {
      lines.push(`- ${v.message}`);
    }
    if (warnings.length > 3) lines.push(`- ...and ${warnings.length - 3} more`);
  }

  lines.push("");
  return lines;
}

/**
 * One-call convenience: check + render + snapshot.
 * Returns null when coherence can't be checked (no filesystem access).
 */
export function checkAndRender(projectRoot: string): {
  lines: string[];
  snapshot: CoherenceSnapshot;
} | null {
  const report = runCoherenceCheck(projectRoot);
  if (!report) return null;

  return {
    lines: renderCoherenceLines(report),
    snapshot: toSnapshot(report),
  };
}

// ── Delta rendering (FEAT-183 / FEAT-184) ────────────────────────────────────

export type CoherenceVerdict = "improved" | "degraded" | "unchanged";

export interface CoherenceDelta {
  before: CoherenceSnapshot;
  after: CoherenceSnapshot;
  gapsFixed: number;
  gapsIntroduced: number;
  netChange: number;
  verdict: CoherenceVerdict;
}

/**
 * Compare two snapshots to produce a delta with verdict.
 * Used by review workflow (FEAT-183) and session wrapup (FEAT-184).
 *
 * Note: `gapsFixed`/`gapsIntroduced` are derived from topGaps (capped at 10),
 * while `netChange` uses totalGaps (uncapped). When many gaps exist, the
 * itemized counts are a lower bound — the render copy reflects this.
 */
export function computeDelta(before: CoherenceSnapshot, after: CoherenceSnapshot): CoherenceDelta {
  const beforeSet = new Set(before.topGaps.map((g) => `${g.registry}::${g.slug}`));
  const afterSet = new Set(after.topGaps.map((g) => `${g.registry}::${g.slug}`));

  let gapsFixed = 0;
  for (const key of beforeSet) {
    if (!afterSet.has(key)) gapsFixed++;
  }
  let gapsIntroduced = 0;
  for (const key of afterSet) {
    if (!beforeSet.has(key)) gapsIntroduced++;
  }

  const netChange = after.totalGaps - before.totalGaps;
  const verdict: CoherenceVerdict =
    netChange < 0 ? "improved" : netChange > 0 ? "degraded" : "unchanged";

  return { before, after, gapsFixed, gapsIntroduced, netChange, verdict };
}

/**
 * Render a coherence delta as markdown lines for review or wrapup.
 * Gate logic: degraded → warning flag, improved → celebration, unchanged → neutral.
 */
export function renderCoherenceDelta(delta: CoherenceDelta): string[] {
  const lines: string[] = ["### Coherence Delta"];

  const p = (n: number) => n === 1 ? "" : "s";
  const absNet = Math.abs(delta.netChange);
  const approx = absNet > delta.gapsFixed + delta.gapsIntroduced;
  const prefix = approx ? "at least " : "";

  if (delta.verdict === "improved") {
    lines.push(
      `Registry coherence **improved**: ${prefix}${delta.gapsFixed} gap${p(delta.gapsFixed)} fixed` +
      (delta.gapsIntroduced > 0 ? `, ${delta.gapsIntroduced} introduced` : "") +
      ` (net ${delta.netChange}). ${delta.after.totalGaps} gap${p(delta.after.totalGaps)} remain${delta.after.totalGaps === 1 ? "s" : ""}.`,
    );
  } else if (delta.verdict === "degraded") {
    lines.push(
      `**Registry coherence degraded**: ${prefix}${delta.gapsIntroduced} new gap${p(delta.gapsIntroduced)} introduced` +
      (delta.gapsFixed > 0 ? `, ${delta.gapsFixed} fixed` : "") +
      ` (net +${delta.netChange}). ${delta.after.totalGaps} gap${p(delta.after.totalGaps)} total.`,
    );
  } else {
    lines.push(
      `Registry coherence unchanged. ${delta.after.totalGaps} gap${p(delta.after.totalGaps)} remain${delta.after.totalGaps === 1 ? "s" : ""}` +
      (delta.gapsFixed > 0 ? ` (${delta.gapsFixed} fixed, ${delta.gapsIntroduced} introduced)` : "") +
      ".",
    );
  }

  lines.push("");
  return lines;
}

/**
 * Coherence gate for review workflow Round 02 (FEAT-183).
 * Produces a pass/fail section comparing session-start snapshot against current state.
 * Gracefully degrades to absolute report when no prior snapshot exists.
 */
export function renderCoherenceGate(
  projectRoot: string,
  sessionSnapshot: CoherenceSnapshot | null,
): { lines: string[]; verdict: CoherenceVerdict | "no-baseline"; afterSnapshot: CoherenceSnapshot | null } {
  const report = runCoherenceCheck(projectRoot);
  if (!report) {
    return {
      lines: ["### Coherence Gate", "Could not run coherence check (filesystem unavailable).", ""],
      verdict: "no-baseline",
      afterSnapshot: null,
    };
  }

  const afterSnapshot = toSnapshot(report);

  if (!sessionSnapshot) {
    const absolute = renderCoherenceLines(report);
    absolute[0] = "### Coherence Gate (no baseline)";
    absolute.splice(1, 0, "_No session-start snapshot available — showing absolute state._");
    return { lines: absolute, verdict: "no-baseline", afterSnapshot };
  }

  const delta = computeDelta(sessionSnapshot, afterSnapshot);
  const lines = renderCoherenceDelta(delta);

  if (delta.verdict === "degraded") {
    lines.push("**Gate: WARN** — This implementation introduced registry drift. Review the new gaps before shipping.");

    const newGaps = delta.after.topGaps.filter(
      (g) => !delta.before.topGaps.some((b) => b.registry === g.registry && b.slug === g.slug),
    );
    if (newGaps.length > 0) {
      lines.push("");
      lines.push("New gaps:");
      for (const g of newGaps.slice(0, 5)) {
        lines.push(`- \`${g.slug}\` missing from \`${g.registry}\` (${g.severity})`);
      }
      if (newGaps.length > 5) lines.push(`- ...and ${newGaps.length - 5} more`);
    }
    lines.push("");
  } else if (delta.verdict === "improved") {
    lines.push("**Gate: PASS** — Nice work, coherence improved this session.");
    lines.push("");
  } else {
    lines.push("**Gate: PASS** — No coherence change.");
    lines.push("");
  }

  return { lines, verdict: delta.verdict, afterSnapshot };
}

// ── Persistent gap detection + tension offers (BET-101 / Phase 6) ────────────

export interface PersistentGap {
  registry: string;
  slug: string;
  severity: RuleSeverity;
  violation: CoherenceViolation | null;
}

/**
 * Find gaps that persisted across the session (present in both before and after).
 * These are candidates for Chain capture as tensions — the session didn't fix them,
 * so they represent structural drift worth tracking.
 */
export function findPersistentGaps(
  delta: CoherenceDelta,
  currentViolations: CoherenceViolation[],
): PersistentGap[] {
  const beforeSet = new Set(delta.before.topGaps.map((g) => `${g.registry}::${g.slug}`));
  const afterSet = new Set(delta.after.topGaps.map((g) => `${g.registry}::${g.slug}`));

  const persistentKeys = new Set<string>();
  for (const key of beforeSet) {
    if (afterSet.has(key)) persistentKeys.add(key);
  }

  if (persistentKeys.size === 0) return [];

  const violationMap = new Map<string, CoherenceViolation>();
  for (const v of currentViolations) {
    violationMap.set(`${v.registryId}::${v.collectionSlug}`, v);
  }

  return [...persistentKeys].map((key) => {
    const [registry = "", slug = ""] = key.split("::", 2);
    return {
      registry,
      slug,
      severity: (delta.after.topGaps.find((g) => g.registry === registry && g.slug === slug)?.severity ?? "warning") as RuleSeverity,
      violation: violationMap.get(key) ?? null,
    };
  });
}

/**
 * Render offer-to-capture section for persistent gaps.
 * Lists each gap with a ready-to-use `capture` command template.
 * BET-101 no-go #3: never auto-create — this is an offer, not an action.
 */
export function renderPersistentGapOffers(gaps: PersistentGap[]): string[] {
  if (gaps.length === 0) return [];

  const lines: string[] = [
    "### Persistent Coherence Gaps",
    "",
    "These gaps existed before and after this session — they represent structural registry drift.",
    "Want me to capture them as tensions on the Chain?",
    "",
  ];

  const SEVERITY_RANK: Record<RuleSeverity, number> = { error: 0, warning: 1, info: 2 };
  const errorsFirst = [...gaps].sort((a, b) =>
    SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity],
  );

  for (const gap of errorsFirst.slice(0, 5)) {
    const v = gap.violation;
    const fixHint = v?.fix ? ` — ${v.fix}` : "";
    lines.push(
      `- **\`${gap.slug}\`** missing from \`${gap.registry}\` (${gap.severity})${fixHint}`,
    );
  }

  if (errorsFirst.length > 5) {
    lines.push(`- ...and ${errorsFirst.length - 5} more`);
  }

  lines.push("");
  lines.push(
    '_Say "capture these as tensions" to accept them onto the Chain, or skip to continue._',
  );
  lines.push("");

  return lines;
}
